/**
 * Client-Side-Router via History-API (Phase 3.5 §4 + §5 Shell-Refactor).
 *
 * NEU: Router rendert in den View-Container der App-Shell (`#view-container`),
 * nicht mehr in `#app` direkt. Shell-Lifecycle (Rail/Sub-Sidebar/Topbar) bleibt
 * persistent zwischen Routen — nur der innere Container wird neu gerendert.
 *
 * Login-Page läuft NICHT durch den Router — sie wird vor mountShell() von
 * main.ts direkt gerendert.
 */

import type { UserContext } from "../api/types";
import { redirectToLogin } from "../auth/login";
import { getViewContainer, setActiveRoute } from "../components/app-shell";
import { escapeHtml } from "../lib/html";

export type RouteParams = Record<string, string>;

export type RouteHandler = (
  host: HTMLElement,
  user: UserContext,
  params: RouteParams
) => Promise<void> | void;

export type LazyRoute = () => Promise<{ render: RouteHandler }>;

// =============================================================================
// User-Cache
// =============================================================================

let cachedUser: UserContext | null = null;

export function setCurrentUser(user: UserContext | null): void {
  cachedUser = user;
}

export function getCurrentUser(): UserContext | null {
  return cachedUser;
}

// =============================================================================
// Route-Registry
// =============================================================================

const routes: Array<{ pattern: string; loader: LazyRoute }> = [];

export function registerRoute(pattern: string, loader: LazyRoute): void {
  routes.push({ pattern, loader });
}

export function _resetRoutesForTests(): void {
  routes.length = 0;
}

/**
 * Alle registrierten Route-Muster (nur für Tests).
 * Existiert für den Nav↔Routen-Parity-Test (E12b-2): ein Navigationseintrag,
 * dessen Route niemand registriert hat, führt ins Leere — und genau so wurde
 * `/api/v1/admin/freshness` zu totem Code, ohne dass es jemandem auffiel.
 */
export function _registeredPatternsForTests(): string[] {
  return routes.map((r) => r.pattern);
}

// =============================================================================
// Pattern-Matcher
// =============================================================================

export function matchRoute(pattern: string, path: string): RouteParams | null {
  const patSegs = pattern.split("/").filter(Boolean);
  const pathSegs = path.split("/").filter(Boolean);
  if (patSegs.length !== pathSegs.length) return null;

  const params: RouteParams = {};
  for (let i = 0; i < patSegs.length; i++) {
    const ps = patSegs[i]!;
    const cs = pathSegs[i]!;
    if (ps.startsWith(":")) {
      params[ps.slice(1)] = decodeURIComponent(cs);
    } else if (ps.toLowerCase() !== cs.toLowerCase()) {
      return null;
    }
  }
  return params;
}

// =============================================================================
// navigate + renderRoute
// =============================================================================

/**
 * ═══ 🔴 DER SUCHTEIL IST NICHT TEIL DES PFADES ═════════════════════════════
 * Hier stand `window.location.pathname !== path` und `renderRoute(path)` mit
 * dem **ganzen** String. Wer `navigate("/kb/termine/klaerfaelle?g=ohne")`
 * aufrief, bekam `?g=ohne` in den Pfad geschrieben — und dann traf
 * `matchRoute` die Route `/kb/termine/klaerfaelle` nicht mehr. Der Aufruf fiel
 * auf die Parameter-Route `/kb/termine/:id` durch, und der Detailschirm
 * meldete: *„Diesen Termin gibt es nicht. Die Kennung `klaerfaelle?g=ohne`
 * führt zu keiner Zeile."*
 *
 * 🔑 **Das erklärt rückwirkend mehr als den einen Klick.** Die Karte
 * „Freigeben" zeigte seit jeher auf `?view=liste&status=vorgemerkt` und kam
 * nie an — ich hatte das am 31.08. allein dem `location.hash` zugeschrieben.
 * Es waren **zwei** Fehler auf demselben Weg: die Ansicht las den Suchteil an
 * der falschen Stelle, **und** der Router ließ ihn gar nicht erst entstehen.
 * *Wer den ersten findet, hört auf zu suchen — und der zweite bleibt liegen.*
 */
function nurPfad(x: string): string {
  return x.split("#")[0]!.split("?")[0]!;
}

/** Nur für Tests — `renderRoute` braucht ein DOM, diese Regel nicht. */
export const _nurPfadForTests = nurPfad;

export async function navigate(path: string): Promise<void> {
  // ⚠️ Verglichen wird Pfad UND Suchteil: sonst bliebe ein Wechsel von
  // `?g=ohne` auf `?g=platz` ohne History-Eintrag, und der Zurück-Knopf
  // übersprünge ihn.
  const jetzt = `${window.location.pathname}${window.location.search}`;
  if (jetzt !== path) {
    window.history.pushState(null, "", path);
  }
  await renderRoute(path);
}

export async function renderRoute(path: string): Promise<void> {
  const user = getCurrentUser();
  if (!user) {
    redirectToLogin();
    return;
  }

  // 🔑 Ab hier zählt nur der Pfad. Den Suchteil liest die View selbst aus
  // `window.location.search` — er ist ein Zustand IN einem Schirm, keine
  // andere Adresse.
  const pfad = nurPfad(path);

  // Shell aktualisieren (Rail-Highlight + Sub-Sidebar + Breadcrumbs)
  setActiveRoute(pfad);

  // R1: View-gebundene UI-Zustände (z. B. das „Daten unvollständig"-Banner)
  // zurücksetzen — jede View startet ohne Alt-Warnungen.
  try { window.dispatchEvent(new CustomEvent("route:willrender", { detail: { path: pfad } })); } catch { /* noop */ }

  const host = getViewContainer();

  for (const { pattern, loader } of routes) {
    const params = matchRoute(pattern, pfad);
    if (params) {
      // 🔑 Laden und Rendern sind ZWEI Dinge. Ein veralteter Chunk-Satz kann
      // nur das Laden treffen; ein Fehler beim Rendern kommt aus der View und
      // darf niemals einen Reload auslösen — sonst lädt die Seite sich unter
      // den Händen des Nutzers neu, statt ihm den Fehler zu zeigen.
      let mod: { render: RouteHandler };
      try {
        mod = await loader();
      } catch (err) {
        if (!sessionStorage.getItem("chunkReloadTried") && (await tabIstVeraltet(err))) {
          sessionStorage.setItem("chunkReloadTried", "1");
          window.location.reload();
          return;
        }
        console.error(`[router] load ${pattern} failed:`, err);
        renderError(host, err);
        host.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }
      // Erfolgreicher Chunk-Load → Stale-Client-Guard zurücksetzen, damit ein
      // späterer Deploy-Zyklus erneut einmalig neu laden darf.
      sessionStorage.removeItem("chunkReloadTried");
      try {
        await mod.render(host, user, params);
      } catch (err) {
        console.error(`[router] render ${pattern} failed:`, err);
        renderError(host, err);
      }
      // Top-of-View scroll bei jedem Render
      host.scrollIntoView({ behavior: "auto", block: "start" });
      return;
    }
  }

  // 🔑 Ohne Suchteil: „unbekannte Adresse /x?y=1" führt den Leser auf die
  // falsche Fährte — der Filter ist nie der Grund, warum eine Route fehlt.
  render404(host, pfad);
}

// =============================================================================
// Link-Interception
// =============================================================================

export function attachLinkInterception(): void {
  document.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    const link = target?.closest("a");
    if (!link) return;

    const href = link.getAttribute("href");
    if (!href || !href.startsWith("/")) return;

    if (link.target === "_blank") return;
    if ((ev as MouseEvent).metaKey || (ev as MouseEvent).ctrlKey || (ev as MouseEvent).shiftKey)
      return;

    ev.preventDefault();
    void navigate(href);
  });
}

// =============================================================================
// Lifecycle
// =============================================================================

export function startRouter(): void {
  window.addEventListener("popstate", () => {
    void renderRoute(window.location.pathname);
  });
  attachLinkInterception();
  void renderRoute(window.location.pathname);
}

// =============================================================================
// 404 + Error-Renderer
// =============================================================================

function render404(host: HTMLElement, path: string): void {
  host.innerHTML = `
    <div class="state-error">
      <h2>404 — Seite nicht gefunden</h2>
      <p>Pfad <code>${escapeHtml(path)}</code> ist nicht registriert.</p>
      <p><a href="/" data-router-link>← Zurück zum Hub</a></p>
    </div>
  `;
}

// =============================================================================
// Veralteter Tab nach einem Deploy — gemessen, nicht am Wortlaut erkannt
// =============================================================================
//
// 🔑 Der Server beantwortet JEDEN unbekannten /assets/-Pfad mit `index.html`
// (200, text/html) — es gibt weder 404-Seite noch `_routes.json`. Der Abruf
// GELINGT also; erst die MIME-Prüfung des Browsers schlägt fehl. Und die
// formulieren die Engines verschieden: WebKit sagt
// `'text/html' is not a valid JavaScript MIME type.`, Chrome und Firefox sagen
// etwas ganz anderes. Bis zum 03.09.2026 stand hier eine Liste von vier
// Wortlauten — die von Chrome und Firefox waren dabei, WebKit nicht. Ergebnis:
// zwei Tester sahen den Fehlerschirm, wo alle anderen still neu geladen hatten.
// 🔑 Wer auf Formulierungen prüft, prüft auf die Browser, die er selbst benutzt.
//
// ⇒ Deshalb wird jetzt GEMESSEN: welches main-Chunk liefert der Server gerade,
//   und welches hat dieser Tab geladen? Ungleich = veralteter Tab — unabhängig
//   davon, wie der Browser den Fehler nennt.

/** Zieht den main-Chunk-Pfad aus einem index.html. `null`, wenn keiner drinsteht. */
export function mainChunkAus(html: string): string | null {
  const m = /<script[^>]+src="(\/assets\/main-[A-Za-z0-9_-]+\.js)"/i.exec(html);
  return m?.[1] ?? null;
}

/**
 * Urteilt über zwei Chunk-Pfade — der reine, ohne Browser prüfbare Teil.
 *
 * ⚠️ Fehlt eine der beiden Angaben, lautet die Antwort `false`. Nicht zu wissen
 * ist kein Grund, dem Nutzer die Seite neu zu laden: 🔑 *eine Selbstheilung, die
 * auch im Blindflug zuschlägt, ist ein Datenverlust mit gutem Gewissen.*
 */
export function istVeralteterTab(
  laufend: string | null,
  serverseitig: string | null
): boolean {
  if (!laufend || !serverseitig) return false;
  return laufend !== serverseitig;
}

/** Das main-Chunk, das DIESER Tab geladen hat — aus dem eigenen Script-Tag. */
function laufendesMainChunk(): string | null {
  try {
    const el = document.querySelector<HTMLScriptElement>(
      'script[type="module"][src*="/assets/main-"]'
    );
    if (!el) return null;
    return new URL(el.src, window.location.origin).pathname;
  } catch {
    return null;
  }
}

/** Das main-Chunk, das der Server GERADE ausliefert. `null` = nicht feststellbar. */
async function serverMainChunk(): Promise<string | null> {
  try {
    const res = await fetch("/", { cache: "reload", credentials: "same-origin" });
    if (!res.ok) return null;
    return mainChunkAus(await res.text());
  } catch {
    return null;
  }
}

/**
 * Ist dieser Tab älter als der ausgelieferte Build?
 *
 * Erste Spur ist die Messung. Der Wortlaut-Abgleich bleibt als ZWEITE Spur:
 * ist der Server gerade nicht erreichbar, ist die Messung blind — dann ist eine
 * erkannte Chunk-Fehlermeldung immer noch besser als nichts.
 */
async function tabIstVeraltet(err: unknown): Promise<boolean> {
  if (istVeralteterTab(laufendesMainChunk(), await serverMainChunk())) return true;
  return isDynamicImportError(err);
}

// Zweite Spur: die bekannten Formulierungen, jetzt inklusive der MIME-Absage.
// Bewusst nicht mehr die einzige — die Liste war der Fehler, nicht ihre Länge.
export function isDynamicImportError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|dynamically imported module|is not a valid JavaScript MIME type|disallowed MIME type|Expected a JavaScript module script/i.test(
    msg
  );
}

function renderError(host: HTMLElement, err: unknown): void {
  const msg = err instanceof Error ? err.message : String(err);
  host.innerHTML = `
    <div class="state-error">
      <h2>Fehler beim Laden der View</h2>
      <p>${escapeHtml(msg)}</p>
      <p><a href="/" data-router-link>← Zurück zum Hub</a></p>
    </div>
  `;
}

